import type { Db } from "../../atomic-crm/providers/fakerest/dataGenerator/types";
import { DEFAULT_RULES } from "../../../../supabase/functions/_shared/automation/defaultRules.ts";
import {
  AI_DEFAULT_RULES,
  AI_REPLACED_RULE_KEYS,
} from "../../../../supabase/functions/_shared/automation/aiRules.ts"; // GAVION ai-email
import { MISSED_CALL_RULES } from "../../../../supabase/functions/_shared/automation/missedCallRules.ts"; // GAVION missed-call
import { demoObjections } from "../objections/fakerestObjections"; // GAVION X2
import { DEFAULT_DEAL_STAGES } from "../domain/deals/stageModel";
import { DEFAULT_LEAD_STATUSES } from "../domain/leads/leadStatusModel";
import {
  SEED_ADDONS,
  SEED_PACKAGES,
} from "../domain/commissions/commissionModel";

// Single source of truth for the demo identity (db row and fakerest login).
export const DEMO_USER = {
  id: 0,
  user_id: "0",
  first_name: "Démo",
  last_name: "Gavion",
  email: "demo@gavion.ai",
  secondary_emails: [] as string[],
  password: "demo",
  administrator: true,
  disabled: false,
} as const;

/** Batch-1 collections that upstream's Db type does not know about. */
export type GavionDemoCollections = {
  invitations: Record<string, unknown>[];
  company_notes: Record<string, unknown>[];
  calls: Record<string, unknown>[];
  suppressions: Record<string, unknown>[];
  consents: Record<string, unknown>[];
  import_batches: Record<string, unknown>[];
};

// GAVION batch 5: email collections (FakeRest twins of the batch-5 tables).
export const GAVION_EMAIL_COLLECTIONS = [
  "email_templates",
  "segments",
  "campaigns",
  "campaign_recipients",
  "sequences",
  "sequence_steps",
  "sequence_enrollments",
  "email_messages",
  "consents",
  "suppressions",
  "integration_accounts",
] as const;

// GAVION batch 6: Granola notes and integration settings (FakeRest twins of 26_gavion_integrations.sql).
export const GAVION_INTEGRATION_COLLECTIONS = [
  "meeting_notes",
  "meeting_note_tasks",
  "api_keys",
  "webhook_endpoints",
  "webhook_deliveries",
] as const;

// GAVION batch 2: dialer and automation collections (calls, suppressions, company_notes and
// integration_accounts are declared above). Empty except the default automation rules (configuration).
export const GAVION_DIALER_COLLECTIONS = [
  "dial_sessions",
  // GAVION requeue fix: « Passer » marks of the day (twin of public.dialer_skips)
  "dialer_skips",
  "automation_runs",
  "automation_events",
  "scheduled_actions",
  "notifications",
  "job_runs",
  // health page reads integrations through this view (granted columns only)
  "v_integration_accounts",
] as const;

// GAVION: batch 7: audit log (FakeRest twin of audit_log / v_audit_log)
export const GAVION_PRIVACY_COLLECTIONS = [
  "audit_log",
  "v_audit_log",
  "storage_purge_queue",
  "v_integration_health",
] as const;

// GAVION ai-email: FakeRest twins of ai_email_drafts, ai_style_profiles, ai_usage (51_gavion_ai_email.sql)
export const GAVION_AI_COLLECTIONS = [
  "ai_email_drafts",
  "ai_style_profiles",
  "ai_usage",
  // GAVION ai follow-up: FakeRest twins of ai_follow_up_plans, ai_follow_up_touches (71_gavion_ai_follow_up.sql)
  "ai_follow_up_plans",
  "ai_follow_up_touches",
  // GAVION call-brief-pipeline: FakeRest twin of call_briefs (84_; id is the row's own, company_id the lead)
  "call_briefs",
] as const;

// GAVION booking: FakeRest twins of booking_settings, bookings, booking_tokens, meeting_reminders (53_gavion_booking.sql).
// booking_settings rows carry id = sales_id (FakeRest needs an id).
export const GAVION_BOOKING_COLLECTIONS = [
  "booking_settings",
  "bookings",
  "booking_tokens",
  "meeting_reminders",
] as const;

// GAVION commissions: FakeRest twins of the 70_gavion_commissions.sql tables. The catalog is configuration (seeded like the
// SQL); everything else starts empty.
export const GAVION_COMMISSION_COLLECTIONS = [
  "packages",
  "package_addons",
  "subscriptions",
  "subscription_periods",
  "subscription_one_time_fees",
  "subscription_month_exceptions",
  "commission_plans",
  "commission_statements",
  "commission_statement_lines",
  "commission_settlements", // 75_: how a negative adjustment was settled with the person
] as const;
const demoCatalog = () => ({
  packages: SEED_PACKAGES.map((p, i) => ({
    ...p,
    id: i + 1,
    included: "",
    active: true,
    created_at: "2026-10-21T00:00:00Z",
  })),
  package_addons: SEED_ADDONS.map((a, i) => ({
    ...a,
    id: i + 1,
    active: true,
    created_at: "2026-10-21T00:00:00Z",
  })),
});

export type GavionDemoDb = Db &
  GavionDemoCollections &
  Record<(typeof GAVION_EMAIL_COLLECTIONS)[number], Record<string, unknown>[]> &
  // GAVION batch 6
  Record<
    (typeof GAVION_INTEGRATION_COLLECTIONS)[number],
    Record<string, unknown>[]
  > &
  // GAVION: batch 7
  Record<
    (typeof GAVION_PRIVACY_COLLECTIONS)[number],
    Record<string, unknown>[]
  > &
  Record<
    (typeof GAVION_DIALER_COLLECTIONS)[number] | "automation_rules",
    Record<string, unknown>[]
  > &
  // GAVION X2: objection library (seeded) and its usage log
  Record<"objections" | "objection_uses", Record<string, unknown>[]> &
  // GAVION training: game mission progress (FakeRest twin of public.training_progress)
  Record<"training_progress", Record<string, unknown>[]> &
  // GAVION rep stats: acquisition cost settings (FakeRest twins of 48_gavion_rep_stats.sql)
  Record<
    "rep_compensation" | "fixed_costs" | "cost_settings",
    Record<string, unknown>[]
  > &
  // GAVION ai-email
  Record<(typeof GAVION_AI_COLLECTIONS)[number], Record<string, unknown>[]> &
  // GAVION booking
  Record<
    (typeof GAVION_BOOKING_COLLECTIONS)[number],
    Record<string, unknown>[]
  > &
  // GAVION commissions
  Record<
    (typeof GAVION_COMMISSION_COLLECTIONS)[number],
    Record<string, unknown>[]
  >;

const emptyDialerCollections = () =>
  Object.fromEntries(
    GAVION_DIALER_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_DIALER_COLLECTIONS)[number],
    Record<string, unknown>[]
  >;

/** The 25 seeded rules (spec 05 §5 + ai-email R20–R24 + missed-call R25): configuration, not sample data. */
const demoRules = () =>
  [...DEFAULT_RULES, ...AI_DEFAULT_RULES, ...MISSED_CALL_RULES].map((r, i) => ({
    ...structuredClone(r),
    id: i + 1,
    // GAVION ai-email (spec §7): R4 and R11 are replaced by the AI drafts (R20, R24)
    enabled: !(AI_REPLACED_RULE_KEYS as readonly string[]).includes(r.key),
    is_default: true,
    description: null,
    updated_at: "2026-10-11T00:00:00Z",
  }));

const emptyEmailCollections = () =>
  Object.fromEntries(
    GAVION_EMAIL_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_EMAIL_COLLECTIONS)[number],
    Record<string, unknown>[]
  >;

// GAVION batch 6
const emptyIntegrationCollections = () =>
  Object.fromEntries(
    GAVION_INTEGRATION_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_INTEGRATION_COLLECTIONS)[number],
    Record<string, unknown>[]
  >;

// Demo mode ships no sample records: every collection is empty except one
// demo sales row so that login works (matches the fakerest login flow).
export const createEmptyDemoDb = (): GavionDemoDb => ({
  // includes batch 1's consents and suppressions (one entry each)
  ...emptyEmailCollections(),
  // GAVION batch 6
  ...emptyIntegrationCollections(),
  // GAVION: batch 7
  ...(Object.fromEntries(
    GAVION_PRIVACY_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_PRIVACY_COLLECTIONS)[number],
    Record<string, unknown>[]
  >),
  ...emptyDialerCollections(),
  // GAVION ai-email
  ...(Object.fromEntries(
    GAVION_AI_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_AI_COLLECTIONS)[number],
    Record<string, unknown>[]
  >),
  // GAVION booking
  ...(Object.fromEntries(
    GAVION_BOOKING_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_BOOKING_COLLECTIONS)[number],
    Record<string, unknown>[]
  >),
  // GAVION commissions: the catalog seeded, the rest empty
  ...(Object.fromEntries(
    GAVION_COMMISSION_COLLECTIONS.map((c) => [c, []]),
  ) as unknown as Record<
    (typeof GAVION_COMMISSION_COLLECTIONS)[number],
    Record<string, unknown>[]
  >),
  ...demoCatalog(),
  automation_rules: demoRules(),
  // GAVION X2
  objections: demoObjections(),
  objection_uses: [],
  // GAVION training
  training_progress: [],
  // GAVION rep stats: no costs until an admin enters them; the cost_settings singleton exists (as after the migration)
  rep_compensation: [],
  fixed_costs: [],
  cost_settings: [{ id: 1, amount_basis: null }],
  companies: [],
  contacts: [],
  contact_notes: [],
  deals: [],
  deal_notes: [],
  sales: [{ ...DEMO_USER, secondary_emails: [] }],
  tags: [],
  tasks: [],
  // GAVION custom pipeline: the default stages with their colours (Paramètres → Pipeline edits them)
  configuration: [
    {
      id: 1,
      config: {
        dealStages: DEFAULT_DEAL_STAGES.map((s) => ({ ...s })),
        // GAVION lead statuses: the default list with its colours (Paramètres → Statuts des leads edits it)
        leadStatuses: DEFAULT_LEAD_STATUSES.map((s) => ({ ...s })),
      } as unknown as Db["configuration"][number]["config"],
    },
  ],
  company_notes: [],
  calls: [],
  import_batches: [],
  // GAVION: Paramètres → Utilisateurs lists pending invitations (FakeRest twin of public.invitations)
  invitations: [],
});
